import { useState } from 'react';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { 
  Shield, 
  Lock, 
  Eye, 
  Database, 
  Mail, 
  Globe,
  FileText,
  ChevronDown,
  ChevronUp
} from 'lucide-react';

export default function PrivacyPolicy() {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const sections = [
    {
      id: 'intro',
      icon: Shield,
      title: 'Introduzione',
      content: `Vault Ride ("noi", "nostro" o "l'App") si impegna a proteggere la privacy dei propri utenti. Questa Privacy Policy descrive come raccogliamo, utilizziamo e proteggiamo le informazioni personali quando utilizzi la nostra applicazione.

**Conformità nLPD**: Questa applicazione è conforme alla nuova Legge federale sulla protezione dei dati (nLPD) della Svizzera, entrata in vigore il 1° settembre 2023.

Ultimo aggiornamento: Febbraio 2026`
    },
    {
      id: 'nlpd',
      icon: Shield,
      title: 'Conformità nLPD Svizzera',
      content: `Vault Ride segue le linee guida della **nuova Legge federale sulla protezione dei dati (nLPD)** della Svizzera:

**Principi fondamentali:**
• **Trasparenza**: Ti informiamo chiaramente su come utilizziamo i tuoi dati
• **Finalità**: I dati sono raccolti solo per scopi specifici e dichiarati
• **Proporzionalità**: Raccogliamo solo i dati strettamente necessari
• **Sicurezza**: Implementiamo misure tecniche e organizzative adeguate

**I tuoi diritti secondo la nLPD:**
• Diritto di accesso ai tuoi dati personali
• Diritto di rettifica dei dati inesatti
• Diritto alla cancellazione dei dati
• Diritto alla portabilità dei dati
• Diritto di opposizione al trattamento

**Responsabile del trattamento:**
Vault Ride
Email: support@vaultride.ch
Paese: Svizzera

Per qualsiasi richiesta relativa ai tuoi dati personali, puoi contattarci e risponderemo entro 30 giorni.`
    },
    {
      id: 'data-collected',
      icon: Database,
      title: 'Dati Raccolti',
      content: `Raccogliamo i seguenti tipi di informazioni:

**Dati di Registrazione:**
• Nome e indirizzo email
• Paese di residenza
• Password (crittografata)

**Dati delle Moto:**
• Marca, modello, anno
• Chilometraggio
• Foto caricate

**Dati di Utilizzo:**
• Spese e manutenzioni registrate
• Viaggi pianificati
• Documenti caricati
• Interazioni nella Community

**Dati di Pagamento:**
• I pagamenti sono gestiti da Stripe
• Non memorizziamo dati delle carte di credito`
    },
    {
      id: 'data-usage',
      icon: Eye,
      title: 'Utilizzo dei Dati',
      content: `Utilizziamo i tuoi dati per:

• Fornire e migliorare i servizi dell'App
• Gestire il tuo account e abbonamento
• Elaborare le statistiche delle tue moto
• Permettere il backup su Google Drive (se autorizzato)
• Inviare notifiche importanti (es. scadenze)
• Rispondere alle tue richieste di supporto

**Non vendiamo** né condividiamo i tuoi dati personali con terze parti per scopi di marketing.`
    },
    {
      id: 'security',
      icon: Lock,
      title: 'Sicurezza dei Dati',
      content: `Proteggiamo i tuoi dati attraverso:

• Crittografia delle password con algoritmi sicuri (bcrypt)
• Connessioni HTTPS per tutti i trasferimenti di dati
• Autenticazione tramite token JWT
• Accesso limitato ai dati da parte del personale
• Backup regolari dei database
• Server sicuri in data center certificati`
    },
    {
      id: 'third-party',
      icon: Globe,
      title: 'Servizi di Terze Parti',
      content: `L'App utilizza i seguenti servizi esterni:

**Stripe** - Elaborazione pagamenti
• Privacy Policy: https://stripe.com/privacy

**Google Drive** - Backup dati (opzionale)
• Privacy Policy: https://policies.google.com/privacy

**OpenRouteService** - Pianificazione percorsi
• Privacy Policy: https://openrouteservice.org/privacy/

**Resend** - Invio email transazionali
• Privacy Policy: https://resend.com/privacy

Questi servizi hanno le proprie policy sulla privacy.`
    },
    {
      id: 'rights',
      icon: FileText,
      title: 'I Tuoi Diritti',
      content: `Hai il diritto di:

• **Accesso**: Richiedere una copia dei tuoi dati
• **Rettifica**: Correggere dati inesatti
• **Cancellazione**: Richiedere l'eliminazione dei tuoi dati
• **Portabilità**: Esportare i tuoi dati in formato leggibile
• **Opposizione**: Opporti al trattamento dei dati

Per esercitare questi diritti, contattaci a: support@vaultride.ch`
    },
    {
      id: 'contact',
      icon: Mail,
      title: 'Contatti',
      content: `Per domande sulla privacy o per esercitare i tuoi diritti:

**Email**: support@vaultride.ch
**Sito Web**: vaultride.ch

Risponderemo entro 30 giorni dalla ricezione della richiesta.`
    }
  ];

  return (
    <Layout>
      <div className="page-container max-w-4xl mx-auto">
        {/* Header */}
        <div className="vault-header mb-8">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-[#FF5F1F]" />
            <div>
              <h1 className="vault-title">Privacy Policy</h1>
              <p className="text-[#9CA3AF]">Come proteggiamo i tuoi dati</p>
            </div>
          </div>
        </div>

        {/* Sections */}
        <div className="space-y-4">
          {sections.map((section) => (
            <div 
              key={section.id}
              className="bg-[#121212] border border-[#262626] rounded-lg overflow-hidden"
            >
              <button
                onClick={() => toggleSection(section.id)}
                className="w-full flex items-center justify-between p-4 text-left hover:bg-[#1a1a1a] transition-colors"
              >
                <div className="flex items-center gap-3">
                  <section.icon className="w-5 h-5 text-[#FF5F1F]" />
                  <span className="text-white font-semibold">{section.title}</span>
                </div>
                {expandedSections[section.id] ? (
                  <ChevronUp className="w-5 h-5 text-[#666]" />
                ) : (
                  <ChevronDown className="w-5 h-5 text-[#666]" />
                )}
              </button>
              
              {expandedSections[section.id] && (
                <div className="px-4 pb-4 pt-0">
                  <div className="border-t border-[#262626] pt-4">
                    <div className="text-[#9CA3AF] whitespace-pre-line text-sm leading-relaxed">
                      {section.content.split('**').map((part, i) => 
                        i % 2 === 1 ? (
                          <strong key={i} className="text-white">{part}</strong>
                        ) : (
                          part
                        )
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="mt-8 p-4 bg-[#121212] border border-[#262626] rounded-lg text-center">
          <p className="text-[#666] text-sm">
            © 2026 Vault Ride. Tutti i diritti riservati.
          </p>
          <p className="text-[#666] text-xs mt-2">
            Utilizzando l'app, accetti questa Privacy Policy.
          </p>
        </div>
      </div>
    </Layout>
  );
}
